import React from "react";
import { Dialog, Box, Typography, DialogContent, DialogActions, Button, CircularProgress, useMediaQuery, Divider, Tab, Tabs } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { Database } from "lucide-react";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { useSettings } from "../../providers/settings-provider";
import { reformatGstDescriptionDates } from "../../util/date";
import theme from "../../util/theme/theme";
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";

interface GstProvisionDetailProps {
    open: boolean;
    provisionTemplateId: number | null;
    onClose: () => void;
}

const GstProvisionDetail: React.FC<GstProvisionDetailProps> = ({ open, provisionTemplateId, onClose }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const rowVirtualizerInstanceRef = React.useRef<any>(null);
    const { formatDate, formatAmount } = useSettings();

    const [template, setTemplate] = React.useState<any>(null);
    const [tabValue, setTabValue] = React.useState<number>(0);
    const [fetchTemplate, setFetchTemplate] = useApi();
    const [fetchTransactions, setFetchTransactions] = useApi();

    React.useEffect(() => {
        if (!open || !provisionTemplateId) return;
        setFetchTemplate(buildApiRequestConfig(routes.provisionTemplate.fetchById, [{ param: 'id', value: provisionTemplateId }]));
        setFetchTransactions(buildApiRequestConfig(routes.provisionTemplate.fetchTransactionsById, [{ param: 'id', value: provisionTemplateId }]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, provisionTemplateId]);

    React.useEffect(() => {
        if (fetchTemplate.data && !fetchTemplate.isLoading) {
            setTemplate(fetchTemplate.data);
        }
    }, [fetchTemplate.data, fetchTemplate.isLoading]);

    const generatedTransactions = fetchTransactions.data?.data || [];

    const columns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'date',
                header: 'Date',
                size: 100,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 10 : 12} fontWeight={600} sx={{ color: 'var(--color-provision-text)' }}>
                        {formatDate(new Date(cell.getValue<string>()))}
                    </Typography>
                ),
            },
            {
                accessorKey: 'amount',
                header: 'Amount',
                size: 100,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 10 : 12} fontWeight={600} sx={{ color: 'var(--color-provision-text)' }}>
                        {formatAmount?.(cell.getValue<number>())}
                    </Typography>
                ),
            },
            {
                accessorKey: 'description',
                header: 'Description',
                size: 260,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 10 : 12} fontWeight={600} sx={{ color: 'var(--color-provision-text)' }}>
                        {reformatGstDescriptionDates(cell.getValue<string>(), formatDate)}
                    </Typography>
                ),
            },
            {
                accessorKey: 'category',
                header: 'Category',
                size: 120,
                Cell: ({ row }: any) => (
                    <Typography fontSize={isMobile ? 10 : 12} fontWeight={600} sx={{ color: 'var(--color-provision-text)' }}>
                        {row.original.category?.name || '-'}
                    </Typography>
                ),
            },
            {
                accessorKey: 'bank',
                header: 'Bank',
                size: 120,
                Cell: ({ row }: any) => (
                    <Typography fontSize={isMobile ? 10 : 12} fontWeight={600} sx={{ color: 'var(--color-provision-text)' }}>
                        {row.original.bankAccount?.accountName || 'No Bank Account'}
                    </Typography>
                ),
            },
        ],
        [formatDate, formatAmount, isMobile]
    );

    return (
        <Dialog
            open={open}
            onClose={() => { onClose(); setTabValue(0); }}
            maxWidth={isMobile ? false : "md"}
            fullWidth
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                    : undefined
            }}
        >
            <DialogContent sx={{ position: 'relative', pt: 3, px: isMobile ? 2 : 3, pb: 0 }}>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: '#607D8B',
                    py: 0.75,
                    pl: 5,
                    pr: 3,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 15% 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 1,
                }}>
                    <Database size={18} color="#fff" /> GST Provision
                </Box>
                {fetchTemplate.isLoading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 200} gap={2} width="100%">
                        <CircularProgress size={40} />
                        <Typography variant="h6" color="text.secondary">Loading provision details...</Typography>
                    </Box>
                ) : (
                    <>
                        <Box display="flex" width="100%">
                            <Box width={200}>
                                <Typography fontSize={isMobile ? 12 : 14} fontWeight={600} color="primary">TEMPLATE</Typography>
                            </Box>
                        </Box>
                        <Box display="flex" width="100%" flexDirection={isMobile ? "column" : "row"} sx={{ px: 0.5, py: 0.75 }}>
                            <Box width={120}><Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">Reporting Cycle</Typography></Box>
                            <Box width={100}><Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">Occurrence</Typography></Box>
                            <Box width={150}><Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">Avg Provision Amount</Typography></Box>
                            <Box width={350}><Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">Description</Typography></Box>
                            {/* <Box width={120}><Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">First Period Ends</Typography></Box> */}
                        </Box>
                        <Box display="flex" width="100%" flexDirection={isMobile ? "column" : "row"} sx={{
                            border: '1px solid #d7d7d7', px: 0.5, py: 0.75, borderRadius: 1, background: 'var(--color-container-background)', color: '#607D8B',
                        }}>
                            <Box width={120}><Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>{template?.type ? template.type.toUpperCase() : '-'}</Typography></Box>
                            <Box width={100}><Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>{template?.occurrenceCount ?? '-'}</Typography></Box>
                            <Box width={150}><Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>{formatAmount?.(template?.amount ?? 0)}</Typography></Box>
                            <Box width={350}><Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>{template?.description}</Typography></Box>
                            {/* <Box width={120}><Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>{template?.startDate ? formatDate(template.startDate) : '-'}</Typography></Box> */}
                        </Box>
                    </>
                )}
            </DialogContent>
            <Divider sx={{ my: 1 }} />
            {!fetchTemplate.isLoading &&
                <DialogContent sx={{ py: 0 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(event, newValue) => setTabValue(newValue)}
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, minHeight: 'auto', '& .MuiTab-root': { minHeight: 32, py: 0.5, px: 1.5, fontSize: '14px' } }}
                    >
                        <Tab label="Provisions" />
                    </Tabs>

                    {tabValue === 0 && (
                        fetchTransactions.isLoading ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                                <CircularProgress size={40} />
                                <Typography variant="h6" color="text.secondary">Loading generated provisions...</Typography>
                            </Box>
                        ) : generatedTransactions.length === 0 ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                                <Typography variant="h6" color="text.secondary">No generated provisions found</Typography>
                            </Box>
                        ) : (
                            <MaterialReactTable
                                columns={columns}
                                data={generatedTransactions}
                                enableRowVirtualization
                                enableColumnVirtualization
                                rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
                                rowVirtualizerOptions={{ overscan: 10 }}
                                columnVirtualizerOptions={{ overscan: 2 }}
                                enablePagination={false}
                                enableTopToolbar={false}
                                enableBottomToolbar={false}
                                enableColumnActions={false}
                                initialState={{ density: 'compact', sorting: [{ id: 'date', desc: false }] }}
                                muiTableContainerProps={{ sx: { height: isMobile ? 150 : 300, overflowY: 'auto' } }}
                                muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                                muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                            />
                        )
                    )}
                </DialogContent>
            }
            <Divider sx={{ my: 1 }} />
            <DialogActions sx={{ justifyContent: 'flex-end', px: isMobile ? 1 : 3, pb: isMobile ? 1 : 2 }}>
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', fontStyle: 'italic', flex: 1 }}>
                    GST provisions are generated and kept in sync automatically from the GST Reporting page — they can't be edited or deleted here.
                </Typography>
                <Button onClick={() => { onClose(); setTabValue(0); }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default GstProvisionDetail;
